/**
 * Snapping a panel tile to its content.
 *
 * Extracted from LCDashboards, where this lived as two inline useCallbacks. The Accounting
 * Dashboards use the shared `usePanelGrid` hook, which never received the behaviour — so
 * double-clicking a resize handle there did nothing, and a count panel narrowed until its
 * blocks reflowed taller just hid the overflow behind a scrollbar. Both dashboards now
 * measure the same way.
 *
 * A panel renders at NATURAL size inside its (overflow-auto) body, so the body's first
 * child is the height and width the tile actually needs.
 */
import type { GridStack, GridItemHTMLElement } from 'gridstack';

import { PANEL_CELL_HEIGHT, PANEL_COLUMNS, PANEL_DRAG_HANDLE_CLASS, PANEL_MARGIN } from './panelGridTypes';

interface Measured {
  /** Header + natural content height, in pixels. */
  contentPx: number;
  /** Natural content width, in pixels. */
  contentWidthPx: number;
}

/** Null when there is nothing to measure — a collapsed tile has no body. */
function measure(itemEl: HTMLElement): Measured | null {
  const content = itemEl.querySelector('.grid-stack-item-content');
  const header = content?.querySelector(`.${PANEL_DRAG_HANDLE_CLASS}`) as HTMLElement | null;
  const body = content?.querySelector('.panel-tile-content') as HTMLElement | null;
  const inner = body?.firstElementChild as HTMLElement | null;
  if (!body || !inner) return null;
  const contentPx = (header?.offsetHeight || 0) + inner.offsetHeight;
  // A tile that is not laid out (hidden tab or workspace) measures zero. Snapping to
  // that would collapse it to nothing, so callers must treat 0 as "don't touch".
  if (contentPx <= 0) return null;
  return { contentPx, contentWidthPx: Math.max(inner.offsetWidth, inner.scrollWidth) };
}

const rowsFor = (contentPx: number) =>
  Math.max(1, Math.ceil((contentPx + 2 * PANEL_MARGIN) / PANEL_CELL_HEIGHT));

/**
 * Snap one tile to fit its content in both dimensions, rounded up to whole grid
 * columns/rows. `heightOnly` keeps the user's width and placement — used by panels whose
 * row count swings with their data. The resulting `grid.update` fires 'change', which is
 * what persists the new size.
 */
export function fitPanelElement(
  grid: GridStack | null,
  gridEl: HTMLElement | null,
  itemEl: HTMLElement,
  heightOnly = false,
): void {
  if (!grid || !gridEl) return;
  const m = measure(itemEl);
  if (!m || !gridEl.clientWidth) return;
  const rows = rowsFor(m.contentPx);
  if (heightOnly) {
    grid.update(itemEl, { h: rows });
    return;
  }
  if (m.contentWidthPx <= 0) return;
  const colPx = gridEl.clientWidth / PANEL_COLUMNS;
  const cols = Math.min(PANEL_COLUMNS,
    Math.max(1, Math.ceil((m.contentWidthPx + 2 * PANEL_MARGIN) / colPx)));
  grid.update(itemEl, { w: cols, h: rows });
}

/**
 * Grow-ONLY height fit. A count panel's blocks reflow into fewer columns as its tile
 * narrows (3 -> 2 -> 1), which multiplies its height; the grid height is fixed, so the
 * overflow would hide behind the body's scrollbar. This adds rows until the panel is
 * visible again and never shrinks, so a height the user dragged taller is preserved.
 *
 * A LOCKED tile is never grown. Locking means "keep this exactly as I put it", and the
 * lock is a real GridStack pin — growing one would have to shove its neighbours. The
 * overflow scrolls inside the body instead, and growth resumes if the user unlocks.
 */
export function growPanelToFitContent(grid: GridStack | null, itemEl: HTMLElement): void {
  if (!grid) return;
  const m = measure(itemEl);
  if (!m) return;
  const node = (itemEl as GridItemHTMLElement).gridstackNode;
  if (node?.locked) return;
  const rows = rowsFor(m.contentPx);
  const current = node?.h ?? parseInt(itemEl.getAttribute('gs-h') || '0', 10);
  if (rows <= current) return;
  grid.update(itemEl, { h: rows });
}

/**
 * Double-clicking the south-east resize handle snaps that tile to fit. Returns the
 * detach function.
 */
export function attachFitOnDoubleClick(
  gridEl: HTMLElement,
  getGrid: () => GridStack | null,
): () => void {
  const onDblClick = (e: MouseEvent) => {
    const handle = (e.target as HTMLElement).closest('.ui-resizable-se');
    if (!handle) return;
    const itemEl = handle.closest('.grid-stack-item') as HTMLElement | null;
    if (itemEl) fitPanelElement(getGrid(), gridEl, itemEl);
  };
  gridEl.addEventListener('dblclick', onDblClick);
  return () => gridEl.removeEventListener('dblclick', onDblClick);
}
